import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Button,
  Space,
  message,
  Card,
  Typography,
  Switch,
  Input,
  Divider,
  Form as AntForm,
} from "@mimber/ui-web"
import { Row, Col, ColorPicker } from "antd"
import type { Color } from "antd/es/color-picker"
import { Form, InputField } from "@mimber/forms"
import {
  useUpdateOrganizationProfile,
  usePublishOrganizationProfile,
  useUnpublishOrganizationProfile,
  useGenerateProfilePreviewToken,
} from "@mimber/client/mimber-organization"
import {
  organizationProfileSchema,
  type OrganizationProfileFormData,
} from "./OrganizationProfileForm.schema"
import type { OrganizationProfileFormProps } from "./OrganizationProfileForm.types"
import {
  SaveOutlined,
  EyeOutlined,
  GlobalOutlined,
  LinkOutlined,
} from "@mimber/ui-web"
import { ProfileStatus } from "@mimber/schema"
import { useState } from "react"

const { Text, Paragraph } = Typography
const { TextArea } = Input

/**
 * OrganizationProfileForm component for managing organization public profile
 * Includes profile details, brand colors, social media links, and publish workflow
 */
export const OrganizationProfileForm: React.FunctionComponent<
  OrganizationProfileFormProps
> = ({ organization, onSuccess, disabled = false }) => {
  const [isPublic, setIsPublic] = useState(organization.isPublic)
  const updateProfile = useUpdateOrganizationProfile(organization.uuid)
  const publishProfile = usePublishOrganizationProfile(organization.uuid)
  const unpublishProfile = useUnpublishOrganizationProfile(organization.uuid)
  const generateToken = useGenerateProfilePreviewToken(organization.uuid)

  const form = useForm<OrganizationProfileFormData>({
    defaultValues: {
      aboutText: organization.aboutText || "",
      industry: organization.industry || "",
      foundedDate: organization.foundedDate
        ? new Date(organization.foundedDate).toISOString().split("T")[0]
        : "",
      taxId: "",
      primaryColor: organization.primaryColor || "#1890ff",
      secondaryColor: organization.secondaryColor || "#52c41a",
      accentColor: organization.accentColor || "#fa541c",
      socialMediaFacebook: organization.socialMediaFacebook || "",
      socialMediaTwitter: organization.socialMediaTwitter || "",
      socialMediaLinkedin: organization.socialMediaLinkedin || "",
      socialMediaInstagram: organization.socialMediaInstagram || "",
      isPublic: organization.isPublic || false,
      publicDescription: organization.publicDescription || "",
      publicContactEmail: organization.publicContactEmail || "",
    },
    resolver: zodResolver(organizationProfileSchema),
    mode: "onBlur",
  })

  const {
    formState: { isDirty },
    reset,
    setValue,
    watch,
  } = form

  const primaryColor = watch("primaryColor")
  const secondaryColor = watch("secondaryColor")
  const accentColor = watch("accentColor")

  const onSubmit = (data: OrganizationProfileFormData) => {
    // Convert date string to Date if provided, clean up empty strings
    const payload = {
      ...data,
      foundedDate: data.foundedDate ? new Date(data.foundedDate) : null,
      socialMediaFacebook: data.socialMediaFacebook || null,
      socialMediaTwitter: data.socialMediaTwitter || null,
      socialMediaLinkedin: data.socialMediaLinkedin || null,
      socialMediaInstagram: data.socialMediaInstagram || null,
      publicContactEmail: data.publicContactEmail || null,
    }

    updateProfile.mutate(payload, {
      onSuccess: () => {
        message.success("Profile updated successfully")
        onSuccess?.()
      },
      onError: (error) => {
        message.error(error.message || "Failed to update profile")
      },
    })
  }

  const handlePreview = () => {
    generateToken.mutate(undefined, {
      onSuccess: (response) => {
        const token = response.data.previewToken
        const slug = organization.slug || "your-organization"
        const mimberAppUrl =
          import.meta.env.VITE_MIMBER_APP_URL || window.location.origin
        const previewUrl = `${mimberAppUrl}/org/${slug}?previewToken=${token}`
        window.open(previewUrl, "_blank")
        message.info("Preview opened in new tab")
      },
      onError: (error) => {
        message.error(error.message || "Failed to generate preview")
      },
    })
  }

  const handlePublish = () => {
    if (!organization.slug) {
      message.error("Organization must have a slug before publishing")
      return
    }

    publishProfile.mutate(undefined, {
      onSuccess: () => {
        message.success("Profile published successfully!")
        onSuccess?.()
      },
      onError: (error) => {
        message.error(error.message || "Failed to publish profile")
      },
    })
  }

  const handleUnpublish = () => {
    unpublishProfile.mutate(undefined, {
      onSuccess: () => {
        message.info("Profile unpublished")
        onSuccess?.()
      },
      onError: (error) => {
        message.error(error.message || "Failed to unpublish profile")
      },
    })
  }

  const isPublished = organization.profileStatus === ProfileStatus.PUBLISHED
  const isLoading = updateProfile.isPending

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={disabled || isLoading}
      layout="vertical"
    >
      {/* Profile Status Banner */}
      {isPublished && (
        <Card
          size="small"
          style={{
            marginBottom: 24,
            background: "#f6ffed",
            borderColor: "#b7eb8f",
          }}
        >
          <Space>
            <GlobalOutlined style={{ color: "#52c41a" }} />
            <Text strong style={{ color: "#52c41a" }}>
              Profile Published
            </Text>
            {organization.slug && (
              <Text type="secondary">
                • Available at: {organization.slug}.app.mimber.com
              </Text>
            )}
          </Space>
        </Card>
      )}

      {/* Basic Information */}
      <Card title="Basic Information" style={{ marginBottom: 24 }}>
        <AntForm.Item label="About Organization" name="aboutText">
          <TextArea
            {...form.register("aboutText")}
            placeholder="Tell your members about your organization..."
            rows={4}
          />
        </AntForm.Item>

        <Row gutter={16}>
          <Col span={12}>
            <InputField
              name="industry"
              label="Industry"
              placeholder="e.g., Technology, Healthcare, Education"
            />
          </Col>
          <Col span={12}>
            <InputField
              name="foundedDate"
              label="Founded Date"
              placeholder="YYYY-MM-DD"
            />
          </Col>
        </Row>

        <InputField
          name="taxId"
          label="Tax ID / EIN"
          placeholder="e.g., 12-3456789"
        />
      </Card>

      {/* Brand Colors */}
      <Card title="Brand Colors" style={{ marginBottom: 24 }}>
        <Paragraph type="secondary">
          Choose colors that represent your organization. These will be applied
          to your public profile.
        </Paragraph>

        <Row gutter={16}>
          <Col span={8}>
            <AntForm.Item label="Primary Color">
              <ColorPicker
                value={primaryColor || undefined}
                onChange={(color: Color) =>
                  setValue("primaryColor", color.toHexString(), {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                showText
                style={{ width: "100%" }}
              />
            </AntForm.Item>
          </Col>
          <Col span={8}>
            <AntForm.Item label="Secondary Color">
              <ColorPicker
                value={secondaryColor || undefined}
                onChange={(color: Color) =>
                  setValue("secondaryColor", color.toHexString(), {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                showText
                style={{ width: "100%" }}
              />
            </AntForm.Item>
          </Col>
          <Col span={8}>
            <AntForm.Item label="Accent Color">
              <ColorPicker
                value={accentColor || undefined}
                onChange={(color: Color) =>
                  setValue("accentColor", color.toHexString(), {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                showText
                style={{ width: "100%" }}
              />
            </AntForm.Item>
          </Col>
        </Row>

        {/* Color Preview */}
        <div
          style={{
            marginTop: 16,
            padding: 16,
            background: "#f5f5f5",
            borderRadius: 8,
          }}
        >
          <Space>
            <Text type="secondary">Preview:</Text>
            {primaryColor && (
              <div
                style={{
                  width: 40,
                  height: 40,
                  background: primaryColor,
                  borderRadius: 4,
                  border: "1px solid #d9d9d9",
                }}
              />
            )}
            {secondaryColor && (
              <div
                style={{
                  width: 40,
                  height: 40,
                  background: secondaryColor,
                  borderRadius: 4,
                  border: "1px solid #d9d9d9",
                }}
              />
            )}
            {accentColor && (
              <div
                style={{
                  width: 40,
                  height: 40,
                  background: accentColor,
                  borderRadius: 4,
                  border: "1px solid #d9d9d9",
                }}
              />
            )}
          </Space>
        </div>
      </Card>

      {/* Social Media Links */}
      <Card title="Social Media" style={{ marginBottom: 24 }}>
        <InputField
          name="socialMediaFacebook"
          label="Facebook"
          placeholder="https://facebook.com/your-organization"
          prefix={<LinkOutlined />}
        />
        <InputField
          name="socialMediaTwitter"
          label="Twitter / X"
          placeholder="https://twitter.com/your-organization"
          prefix={<LinkOutlined />}
        />
        <InputField
          name="socialMediaLinkedin"
          label="LinkedIn"
          placeholder="https://linkedin.com/company/your-organization"
          prefix={<LinkOutlined />}
        />
        <InputField
          name="socialMediaInstagram"
          label="Instagram"
          placeholder="https://instagram.com/your-organization"
          prefix={<LinkOutlined />}
        />
      </Card>

      {/* Public Profile Settings */}
      <Card title="Public Profile Settings" style={{ marginBottom: 24 }}>
        <AntForm.Item
          label="Make Profile Public"
          tooltip="When enabled, your profile will be visible to anyone with the link"
        >
          <Switch
            checked={isPublic}
            onChange={(checked) => {
              setIsPublic(checked)
              setValue("isPublic", checked, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }}
          />
        </AntForm.Item>

        <AntForm.Item label="Public Description" name="publicDescription">
          <TextArea
            {...form.register("publicDescription")}
            placeholder="Brief description for public profile"
            rows={2}
          />
        </AntForm.Item>

        <InputField
          name="publicContactEmail"
          label="Public Contact Email"
          placeholder="contact@your-organization.com"
          type="email"
        />
      </Card>

      {/* Action Buttons */}
      <Space size="middle">
        <Button
          type="primary"
          htmlType="submit"
          icon={<SaveOutlined />}
          disabled={disabled || !isDirty}
          loading={isLoading}
        >
          Save Changes
        </Button>

        <Button
          onClick={() => reset()}
          disabled={disabled || !isDirty || isLoading}
        >
          Reset
        </Button>

        <Divider type="vertical" />

        <Button
          icon={<EyeOutlined />}
          onClick={handlePreview}
          loading={generateToken.isPending}
          disabled={!organization.slug}
        >
          Preview Profile
        </Button>

        {isPublished ? (
          <Button
            onClick={handleUnpublish}
            loading={unpublishProfile.isPending}
            danger
          >
            Unpublish
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<GlobalOutlined />}
            onClick={handlePublish}
            loading={publishProfile.isPending}
            disabled={disabled || !organization.slug}
          >
            Publish Profile
          </Button>
        )}
      </Space>
    </Form>
  )
}
